import { useMemo } from 'react';
import { useFormattedDate } from './useFormattedDate';
import { isSameMonth, isSameYear } from 'date-fns';
import { useTranslation } from '@shared/core';

type DateInput = Date | number | string | null | undefined;

type FormattedRangeOptions = Readonly<{
  /**
   * Whether to include time formatting (e.g., "2:00 PM to 4:30 PM")
   * If true, displays only the time range without dates
   */
  includeTime?: boolean;
}>;

/**
 * @deprecated Use `useEventLocaleFormatter().formatDateRange()` instead.
 *
 * The new approach provides the same functionality with better consistency:
 *
 * @example
 * ```tsx
 * // Old way
 * const dateRange = useFormattedDateRange(startDate, endDate);
 * const timeRange = useFormattedDateRange(startTime, endTime, { includeTime: true });
 *
 * // New way (preferred)
 * const { formatDateRange } = useEventLocaleFormatter();
 * const dateRange = formatDateRange(startDate, endDate);
 * const timeRange = formatDateRange(startTime, endTime, { includeTime: true });
 * ```
 *
 * ---
 *
 * Hook for formatting date and time ranges.
 * Optimizes display for same-month and same-year cases.
 *
 * USAGE EXAMPLES:
 * Date-only ranges (default):
 * - Same month: "Jan 15 - 20, 2025"
 * - Different months, same year: "Jan 15 - Feb 20, 2025"
 * - Different years: "Dec 20, 2024 - Jan 5, 2025"
 * - Single date (no end): "Jan 15, 2025"
 *
 * Time-only ranges (with includeTime: true):
 * - Time range: "2:00 PM to 4:30 PM"
 * - Single time (no end): "2:00 PM"
 * - No times: undefined
 *
 * USE THIS FOR:
 * - Hotel check-in/check-out dates
 * - Accommodation bookings
 * - Event date ranges without specific times
 * - Same-day time ranges (with includeTime: true)
 * - Shuttle/transportation time ranges (with includeTime: true)
 *
 * DO NOT USE FOR:
 * - Schedule events with times and dates (use useFormattedDateRange instead)
 * - Events with timezone handling (use useFormattedDateRange instead)
 *
 * @param startDate - Start date/time (Date object, timestamp, or null)
 * @param endDate - End date/time (Date object, timestamp, or null)
 * @param options - Formatting options
 * @returns Formatted date or time range string
 */
export const useFormattedDateRange = (startDate: DateInput, endDate: DateInput, options: FormattedRangeOptions = {}): string | undefined => {
  const { includeTime = false } = options;

  const { tq } = useTranslation('dates');

  // For time-only formatting
  const startTime = useFormattedDate(startDate, 'hh:mm');
  const endTime = useFormattedDate(endDate, 'hh:mm');

  // For date-only formatting
  const startMonthDay = useFormattedDate(startDate, 'MMMdd');
  const endDay = useFormattedDate(endDate, 'dd');
  const endMonthDay = useFormattedDate(endDate, 'MMMdd');
  const startYear = useFormattedDate(startDate, 'yyyy');
  const endYear = useFormattedDate(endDate, 'yyyy');

  // Convert to Date objects for comparison
  const startDateObj = useMemo(() => {
    if (!startDate) return null;
    if (startDate instanceof Date) return startDate;
    if (typeof startDate === 'number') return new Date(startDate);
    return new Date(startDate);
  }, [startDate]);

  const endDateObj = useMemo(() => {
    if (!endDate) return null;
    if (endDate instanceof Date) return endDate;
    if (typeof endDate === 'number') return new Date(endDate);
    return new Date(endDate);
  }, [endDate]);

  return useMemo(() => {
    // Time-only formatting
    if (includeTime) {
      if (!startTime) return undefined;
      if (!endTime) return startTime;
      return tq`${startTime} to ${endTime}`;
    }

    // Date-only formatting
    if (!startDateObj) return '';

    // Single date (no end date)
    if (!endDateObj) {
      return `${startMonthDay}, ${startYear}`;
    }

    const sameMonth = isSameMonth(startDateObj, endDateObj);
    const sameYear = isSameYear(startDateObj, endDateObj);

    // Same month and year: "Jan 15 - 20, 2025"
    if (sameMonth && sameYear) {
      return `${startMonthDay} - ${endDay}, ${startYear}`;
    }

    // Different months, same year: "Jan 15 - Feb 20, 2025"
    if (!sameMonth && sameYear) {
      return `${startMonthDay} - ${endMonthDay}, ${startYear}`;
    }

    // Different years: "Dec 20, 2024 - Jan 5, 2025"
    return `${startMonthDay}, ${startYear} - ${endMonthDay}, ${endYear}`;
  }, [includeTime, startTime, endTime, tq, startDateObj, endDateObj, startMonthDay, endDay, endMonthDay, startYear, endYear]);
};
